<template>
	<header-block :title="insuranceHeader" bgcolor="white" border="0"></header-block>
	<CustomerLoadWait :customer="customer && policyTypes"/>
	<div class="progress-container" v-if="totalPages > 1">
		<v-progress-linear :model-value="progressValue" rounded></v-progress-linear>
	</div>		

	<transition>
		<div v-if="customer && policyTypes && (policy || !this.$route.params.id) ">
			<div class="personalDetails">
				<InputSections ref="inputSections" :sections="sections" theme="light"/>
				<div v-if="validationText" class="well">
					<p>{{ validationText }}</p>
				</div>
				<div class="anchor-bottom submitControls">
					<v-row>
						<v-col cols="5" v-if="currentPageNumber > 0"><MGButton small="true" :outline="true" @click="_inputControl_GoBack()" text="Back" /></v-col>
						<v-col><MGButton :small="currentPageNumber > 0" :alt="false" text="Next" :arrow="true" :fullWidth="true" :disabled="!isCurrentSectionValid" @click="_inputControl_GoNext(addPolicy, _preValidate)"/></v-col>
					</v-row>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import Helpers from '../mixins/Helpers';
import Services from '../mixins/Services';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import InputSections from './elements/InputSections.vue';
import MGButton from './elements/MGButton.vue';

export default {
	mixins: [Services, Helpers],
	components: {HeaderBlock, InputSections, MGButton, CustomerLoadWait},
	data() {
		return {
			validationText: null,
			sectionNo: 0,
			currentPageNumber: 0,
			sections: []
		}
	},
	computed: {
		insuranceHeader() {
			if (!this.sections || !this.sections[0] || !this.sections[0].fields || !this.sections[0].fields[0] || !this.sections[0].fields[0].value) {
				return (this.$route.params.id ? 'Edit' : 'Add') + " insurance details";
			}
			var title = (this.$route.params.id ? 'Edit' : 'Add') + " " + this.sections[0].fields[0].value + " Insurance details";
			return title;
		},
		progressValue() {
			if (this.totalPages === 0) return 0;
			return ((this.currentPageNumber + 1) / this.totalPages) * 100;
		},
		totalPages() {
			var insuranceType = this._findFieldValue('Policy type');
			return this.sectionsAvailableCount(insuranceType);
		},
		isCurrentSectionValid() {
			if (!this.sections || !this.sections[this.sectionNo] || !this.sections[this.sectionNo].fields) {
				return false;
			}
			const currentFields = this.sections[this.sectionNo].fields;
			for (const field of currentFields) {
				if (field.required && (!field.value || field.value === "")) {
					return false;
				}
			}
			return true;
		},
	},
	methods: {
		onLoad: async function() {
			this._getAiopSection('InsuranceMainSections.json', () => {
				this.sections = this.sectionJson;
				if (this.$route.params.id) {
					this._getPolicy(this.$route.params.id, (policy) => {
						this.policy = policy;
						this.populateFields();
					});
				}
				this._getCustomer();
				this._getInsuranceProviders((res) => {
					res = res.filter(item => item.buildings === true || item.contents === true || item.gadget === true
							|| item.car === true || item.motorbike === true || item.pet === true
							|| item.life === true || item.other === true)
							.filter((item, index, self) =>
							index === self.findIndex((t) => t.name === item.name)
						);
					var providerField = this._findFieldById('provider');
					providerField.items = res.map(provider => ({
						name: provider.name.replace(/\w\S*/g, (txt) => {
							return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
						}),
						logoUri: provider.logoUri
					}));
				});
				this._getPolicyTypes((res) => {
					var policyTypeField = this._findFieldById('policyType');
					policyTypeField.items = res.map(policyType => {
						return policyType.name.replace(/\w\S*/g, (txt) => {
							return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
						});
					});
				});
			});
		},
		populateFields() {
			const fieldMappings = {
				'Policy type': this.policy.policyType.name,
				'Provider': this.policy.provider.name,
				'Policy number': this.policy.policyNumber,
				'Start date': this._dateToString(this.policy.startDate),
				'Renewal date': this._dateToString(this.policy.endDate),
				'Premium': this.policy.annualPremium || this.policy.monthlyPremium,
				'Excess': this.policy.excess,
				'No claims (years)': this.policy.noClaimsYears,
				'Sole or joint': this.policy.soleOrJoint,
				'Sum assured': this.policy.sumAssured,
				'Cover type': this.policy.coverType,
				'Type': this.policy.travelCoverType,
				'Coverage': this.policy.travelCoverage,
				'How do you pay': this.policy.annualPremium ? 'Annually' : 'Monthly',
				'Address': this.policy.insuredItems[0]?.addressInsured,
				'Make': this.policy.insuredItems[0]?.vehicleMake,
				'Model': this.policy.insuredItems[0]?.vehicleModel,
				'Registration': this.policy.insuredItems[0]?.vehicleRegistration,
				'Name': this.policy.insuredItems[0]?.name,
				'Value insured': this.policy.insuredItems[0]?.valueInsured,
				'Breed': this.policy.insuredItems[0]?.petBreed,
				'Date of birth': this._dateToString(this.policy.insuredItems[0]?.petBirthdate),
				'Device Type': this.policy.insuredItems[0]?.itemType
			};

			this._loadFieldsFromObject(fieldMappings);
		},
		addPolicy() {
			const policyType = {
				name: this._findFieldValueById('policyType') || null,
			};
			const provider = {
				name: this._findFieldValueById('provider') || null,
			};
			var addressObject = this._findFieldValue('Address') || null;
			const addressInsured = {
				line1: addressObject?.line1 || null,
				line2: addressObject?.line2 || null,
				line3: addressObject?.line3 || null,
				town: addressObject?.town || null,
				postCode: addressObject?.postCode || null,
			};
			const insuredItems = [];
			const insuredItem = ({
				itemType: this._findFieldValueById('policyType') || null,
				addressInsured: addressInsured || null,
				name: this._findFieldValueById('name') || null,
				petBreed: this._findFieldValueById('breed') || null,
				petBirthDate: this._stringToDate(this._findFieldValueById('dateOfBirth')) || new Date('1900-01-01'),
				vehicleMake: this._findFieldValueById('make') || null,
				vehicleModel: this._findFieldValueById('model') || null,
				vehicleRegistration: this._findFieldValueById('registration') || null,
				valueInsured: this._findFieldValueById('valueInsured') || 0,
			});
			insuredItems.push(insuredItem);
			const policy = ({
				policyType: policyType,
				policyNumber: this._findFieldValueById('policyNumber') || null,
				provider: provider,
				insuredItems: insuredItems,
				payFrequency: this._findFieldValueById('payFrequency') || null,
				startDate: this._stringToDate(this._findFieldValueById('startDate')) || null,
				endDate: this._stringToDate(this._findFieldValueById('renewalDate')) || null,
				annualPremium: this._findFieldValueById('payFrequency') === 'Annually' ? this._findFieldValueById('premium') : null,
				monthlyPremium: this._findFieldValueById('payFrequency') === 'Monthly' ? this._findFieldValueById('premium') : null,
				excess: this._findFieldValueById('excess') || null,
				noClaimsYears: this._findFieldValueById('noClaimsYears') || null,
				soleOrJoint: this._findFieldValueById('soleOrJoint') || null,
				sumAssured: this._findFieldValueById('sumAssured') || null,
				coverType: this._findFieldValueById('coverType') || null,
				travelCoverType: this._findFieldValueById('type') || null,
				travelCoverage: this._findFieldValueById('coverage') || null,
			});

			if (this.$route.params.id) {
				policy.id = this.$route.params.id;
			}

			const handleResponse = (res) => {
				if (res) {
					if (this.$route.params.id) {
						this._getPolicy(this.$route.params.id, /*onSuccess*/ null, /*refreshCache*/ true);
					}
					this._getInsuranceInfo(/*onSuccess*/ null, /*refreshCache*/ true);
					this.$router.push({ path: '/insurancesuccess', query: { policyId: res.id } });
				}
			};

			if (!this.$route.params.id) {
				this._addPolicy(policy, handleResponse);
			} else {
				this._updatePolicy(policy, handleResponse);
			}
		},
		sectionsAvailableCount: function(insuranceType) {
			var sectionsNoRule = this.sections.filter(section => !section.rule);
			var sectionsMatchingRule = this.sections.filter(section => section.rule && section.rule.includes(insuranceType));
			return sectionsNoRule.length + sectionsMatchingRule.length;
		}
	},
	mounted: function () {
		this.onLoad();
	},
}
</script>

<style scoped>


:deep(.check .v-label) {
	color: white !important;
}

.well {
	text-align: center;
	background-color: #3328470D;
}

.progress-container {
	display: flex;
	justify-content: center;
	margin: 20px 0;
}

:deep(.v-progress-linear__background) {
  background-color: var(--border-color) !important;
}

</style>
